import { deleteServerColumns, deleteServerRows, fetchServerFilteredViewport, fetchServerViewport, sortServerTable } from '../core/apiClient.js';
import type { Command, CommandResult } from '../core/command/index.js';
import type { FilterCondition, SetFilterCommand, SortCommand, StructuralCommand } from '../core/command/types.js';
import type { Range, SheetId } from '../core/types.js';
import {
  ALIGN_LEFT,
  ALIGN_RIGHT,
  FALLBACK_STYLE,
  FLAG_EMPTY,
  type SheetView,
  type ViewTile,
} from '../grid/viewTile.js';
import type { GridDataSource } from './gridView.js';

interface RemoteViewportResponse {
  ok: boolean;
  columns?: string[];
  rows?: unknown[][];
  error?: string;
}

/**
 * Adapts a server-side DuckDB table to the canvas grid's bounded tile contract.
 * It intentionally has no local data cache: the tile cache owns viewport caching
 * and only the visible range crosses the network.
 */
export function createRemoteDuckDbSource(options: {
  table: string;
  sheet?: SheetId;
  name?: string;
  rowCount: number;
  columnCount: number;
  fallback: GridDataSource;
}): GridDataSource {
  const name = options.name ?? options.table;
  let activeFilter: { column: number; condition: FilterCondition } | null = null;

  const unsupported = (command: Command): CommandResult => ({
    ok: false,
    command,
    error: {
      correlationId: `remote-${Date.now().toString(36)}`,
      subsystem: 'command',
      kind: 'unsupported',
      message: 'This operation is not available for a remote DuckDB dataset yet.',
      cancelled: false,
      at: Date.now(),
    },
  });

  const remoteDelete = async (command: StructuralCommand): Promise<CommandResult> => {
    const started = performance.now();
    const at = command.at;
    const count = command.count;
    const sheet = command.sheet;
    const response = await deleteServerRows(options.table, at, at + count - 1);
    const payload = await response.json() as { ok?: boolean; error?: string };
    if (!response.ok || !payload.ok) return unsupported(command);
    return {
      ok: true,
      command,
      label: 'Remote DuckDB row delete',
      txn: Date.now(),
      changed: [],
      changedRegions: [{ sheet, r0: at, c0: 0, r1: at + count - 1, c1: options.columnCount - 1 }],
      overrides: [],
      spills: [],
      cycles: [],
      notices: [`Deleted ${payload.ok ? count : 0} rows in DuckDB.`],
      stats: { evaluated: 0, elapsedMs: performance.now() - started },
    };
  };

  const remoteSort = async (command: SortCommand): Promise<CommandResult> => {
    const started = performance.now();
    const response = await sortServerTable(options.table, command.keys);
    const payload = await response.json() as { ok?: boolean };
    if (!response.ok || !payload.ok) return unsupported(command);
    return {
      ok: true,
      command,
      label: 'Remote DuckDB sort',
      txn: Date.now(),
      changed: [],
      changedRegions: [command.range],
      overrides: [],
      spills: [],
      cycles: [],
      notices: ['Sorted in server-side DuckDB.'],
      stats: { evaluated: 0, elapsedMs: performance.now() - started },
    };
  };

  const remoteDeleteColumns = async (command: StructuralCommand): Promise<CommandResult> => {
    const started = performance.now();
    const response = await deleteServerColumns(options.table, command.at, command.count);
    const payload = await response.json() as { ok?: boolean; error?: string };
    if (!response.ok || !payload.ok) return unsupported(command);
    return {
      ok: true, command, label: 'Remote DuckDB column delete', txn: Date.now(), changed: [],
      changedRegions: [{ sheet: command.sheet, r0: 0, c0: command.at, r1: options.rowCount - 1, c1: command.at + command.count - 1 }],
      overrides: [], spills: [], cycles: [], notices: [`Deleted ${command.count} columns in DuckDB.`],
      stats: { evaluated: 0, elapsedMs: performance.now() - started },
    };
  };

  const remoteFilter = async (command: SetFilterCommand): Promise<CommandResult> => {
    activeFilter = { column: command.col, condition: command.cond };
    return {
      ok: true,
      command,
      label: 'Remote DuckDB filter',
      txn: Date.now(),
      changed: [],
      changedRegions: [{ sheet: command.sheet, r0: 0, c0: 0, r1: options.rowCount - 1, c1: options.columnCount - 1 }],
      overrides: [],
      spills: [],
      cycles: [],
      notices: ['Filtered in server-side DuckDB.'],
      stats: { evaluated: 0, elapsedMs: 0 },
    };
  };

  return {
    async view(range: Range): Promise<ViewTile> {
      const response = activeFilter === null
        ? await fetchServerViewport(options.table, range.r0, range.r1 - range.r0 + 1, '*')
        : await fetchServerFilteredViewport(options.table, activeFilter.column, activeFilter.condition, range.r0, range.r1 - range.r0 + 1);
      const payload = await response.json() as RemoteViewportResponse;
      if (!response.ok || !payload.ok || !payload.rows || !payload.columns) {
        throw new Error(payload.error || `Remote viewport request failed (${response.status})`);
      }
      const rows = range.r1 - range.r0 + 1;
      const cols = range.c1 - range.c0 + 1;
      const text: string[] = [];
      const align = new Uint8Array(rows * cols);
      const flags = new Uint8Array(rows * cols);
      const styleRefs = new Int32Array(rows * cols).fill(0);
      for (let r = 0; r < rows; r += 1) {
        const sourceRow = payload.rows[r] ?? [];
        for (let c = 0; c < cols; c += 1) {
          const value = sourceRow[range.c0 + c] ?? null;
          const index = r * cols + c;
          text.push(value === null ? '' : String(value));
          if (flags[index] !== undefined && (value === null || value === '')) flags[index] |= FLAG_EMPTY;
          align[index] = typeof value === 'number' ? ALIGN_RIGHT : ALIGN_LEFT;
        }
      }
      return { range, rows, cols, text, align, flags, styleRefs, styles: [FALLBACK_STYLE] };
    },

    async sheetView(currentSheet: SheetId): Promise<SheetView> {
      return {
        sheet: currentSheet,
        name,
        index: 0,
        sheets: [{ id: currentSheet, name, index: 0 }],
        colWidths: [],
        rowHeights: [],
        defaultColWidthPx: 110,
        defaultRowHeightPx: 22,
        freeze: null,
        merges: [],
        usedRange: { sheet: currentSheet, r0: 0, c0: 0, r1: Math.max(0, options.rowCount - 1), c1: Math.max(0, options.columnCount - 1) },
        hiddenRows: [],
      };
    },

    dispatch: async (command: Command) => {
      if (command.k === 'deleteRows') {
        return remoteDelete(command as Extract<StructuralCommand, { k: 'deleteRows' }>);
      }
      if (command.k === 'deleteCols') {
        return remoteDeleteColumns(command as Extract<StructuralCommand, { k: 'deleteCols' }>);
      }
      if (command.k === 'sort') {
        return remoteSort(command as SortCommand);
      }
      if (command.k === 'setFilter') {
        return remoteFilter(command as SetFilterCommand);
      }
      if (command.k === 'clearFilters') {
        activeFilter = null;
        return {
          ok: true,
          command,
          label: 'Clear remote DuckDB filter',
          txn: Date.now(),
          changed: [],
          changedRegions: [{ sheet: command.sheet, r0: 0, c0: 0, r1: options.rowCount - 1, c1: options.columnCount - 1 }],
          overrides: [],
          spills: [],
          cycles: [],
          notices: ['Remote filter cleared.'],
          stats: { evaluated: 0, elapsedMs: 0 },
        };
      }
      return unsupported(command);
    },
    dispatchBatch: async (commands: readonly Command[]) => unsupported(commands[0]!),
    ...(options.fallback.cellContent ? { cellContent: options.fallback.cellContent } : {}),
    ...(options.fallback.blockEdge ? { blockEdge: options.fallback.blockEdge } : {}),
    ...(options.fallback.lastUsedCell ? { lastUsedCell: options.fallback.lastUsedCell } : {}),
    ...(options.fallback.undo ? { undo: options.fallback.undo } : {}),
    ...(options.fallback.redo ? { redo: options.fallback.redo } : {}),
  } as unknown as GridDataSource;
}
